import React, { memo, FC } from "react";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ReusableAccordion from "../ReusableAccordion";
import { Image } from "cloudinary-react";
import {
  lockrailEven,
} from "./defaults";
import { showStilesOptions } from "./showStilesOptions";
import { showStilesOptions2ndLevel } from "./showStilesOptionsSecondLevel";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useStilesRailsSelector from "./useStilesRailsSelector";
import Video from "../Video";

const StileLabel = styled(Typography)({
  position: "relative",
  zIndex: 10,
  top: 25,
  fontWeight: "bold",
});

const StyledDivImgContainer = styled("div")({
  marginLeft: 10,
  marginRight: 10,
  display: "flex",
  justifyContent: "center",
});

const RowLayoutTopDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  borderTop: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  justifyContent: "center",
}));

const ColLayoutTopDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
}));

export function picoWindowEvenSide(config: string) {
  return config
    .split("")
    .filter((char) => !isNaN(Number(char)))
    .some((char) => Number(char) % 2 === 0);
}

interface StilesRailsProps {
  evenConfig?: boolean;
}

const StilesRailsAccordian: FC<StilesRailsProps> = ({ evenConfig = false }) => {
  const {
    stiles,
    stileCategory,
    dsi,
    dsi2,
    dsi3,
    label1,
    label2,
    label3,
    showDisclaimerStiles,
    showTradBtms,
    showLockRail,
    handleStilesClick1stLevel,
    handleStilesClick2ndLevel,
    showLabels,
    showImageLabels,
    materialChoice,
    doorType,
    panelErr,
    chosenConfig,
    showSlimline,
    bifoldConfiguration,
    accoyaChosen,
    overallHeight,
    isSwing,
    isPivot,
    isSlider,
    isBifold,
    isBifoldWindow,
    isFixedPanel
  } = useStilesRailsSelector(evenConfig);

  return (
    <ReusableAccordion
      accordionName="stilesRails"
      heading="Stiles and Rails"
      secondaryHeading="Select desired railing"
      colOrRow="col"
    >
      {showDisclaimerStiles &&
        materialChoice === "clad" &&
        isSwing && (
          <Typography variant="subtitle1">
            * I89 must be added to meet Title 24
          </Typography>
        )}
      {(isSlider || isPivot) && panelErr.slimline && (
        <Alert severity="error" style={{ marginBottom: 10, color: "black" }}>
          {panelErr.slimline}
        </Alert>
      )}
      {isPivot && panelErr.euro && (
        <Alert severity="error" style={{ marginBottom: 10, color: "black" }}>
          {panelErr.euro}
        </Alert>
      )}

      {isBifoldWindow &&
        materialChoice === "clad" &&
        overallHeight &&
        overallHeight < 38 && (
          <Alert severity="error" style={{ marginBottom: 10, color: "black" }}>
            <img
              alt="pico txt"
              src={`https://res.cloudinary.com/ag-millworks/image/upload/Pico_Text/svg/pico_black.svg`}
              style={{ width: 40, height: "auto", paddingRight: 3 }}
            />{" "}
            Stile unavailable when frame height is under 38"
          </Alert>
        )}

      <RowLayout>
        {showStilesOptions(
          doorType,
          materialChoice,
          stileCategory || "",
          handleStilesClick1stLevel,
          chosenConfig,
          panelErr,
          overallHeight
        )}
      </RowLayout>

      {showSlimline &&
      (materialChoice === "clad" || isBifoldWindow) ? (
        <RowLayoutTopDivider>
          {showStilesOptions2ndLevel(
            doorType,
            materialChoice,
            "slim",
            handleStilesClick2ndLevel,
            stiles || ""
          )}
        </RowLayoutTopDivider>
      ) : showTradBtms && stileCategory ? (
        <RowLayoutTopDivider>
          {showStilesOptions2ndLevel(
            doorType,
            materialChoice,
            "trad",
            handleStilesClick2ndLevel,
            stiles || ""
          )}
        </RowLayoutTopDivider>
      ) : null}

      {showLockRail &&
        ((materialChoice !== "wood" &&
          !accoyaChosen &&
          !evenConfig &&
          !isBifoldWindow) ||
          (isBifoldWindow &&
            (bifoldConfiguration === "3L OR 3R" ||
              bifoldConfiguration === "5L OR 5R"))) && (
          <ColLayoutTopDivider>
            {(stiles === "Slimline Traditional" || materialChoice === "wood") &&
              chosenConfig !== "Fixed Full Bound" &&
              !isBifold && (
                <Typography style={{ fontWeight: "bold" }} variant="subtitle2">
                  * Title 24 numbers not available
                </Typography>
              )}
          </ColLayoutTopDivider>
        )}

      {showLockRail &&
        ((materialChoice !== "wood" &&
          !accoyaChosen &&
          evenConfig &&
          !isBifoldWindow) ||
          (isBifoldWindow &&
            bifoldConfiguration !== "3L OR 3R" &&
            bifoldConfiguration !== "5L OR 5R")) && (
          <ColLayoutTopDivider>
            {!isBifoldWindow && !isFixedPanel && (
              <Typography variant="subtitle1">
                {lockrailEven.label} is required with even configuration
              </Typography>
            )}

            {(stiles === "Slimline Traditional" || materialChoice === "wood") &&
              chosenConfig !== "Fixed Full Bound" &&
              !isBifold && (
                <Typography style={{ fontWeight: "bold" }} variant="subtitle2">
                  * Title 24 numbers not available
                </Typography>
              )}
          </ColLayoutTopDivider>
        )}

      {stiles && dsi && (
        <RowLayout style={{ marginTop: -20 }}>
          <ColLayout>
            {showLabels && <StileLabel variant="body2">{label1}</StileLabel>}
            <StyledDivImgContainer>
              <Image
                cloudName="ag-millworks"
                secure="true"
                publicId={`StilesAndRails/${dsi}`}
                onLoad={showImageLabels}
              />
            </StyledDivImgContainer>
          </ColLayout>

          {dsi2 && (
            <ColLayout>
              {showLabels && <StileLabel variant="body2">{label2}</StileLabel>}
              <StyledDivImgContainer>
                <Image
                  cloudName="ag-millworks"
                  secure="true"
                  publicId={`StilesAndRails/${dsi2}`}
                />
              </StyledDivImgContainer>
            </ColLayout>
          )}

          {dsi3 && (
            <ColLayout>
              {showLabels && <StileLabel variant="body2">{label3}</StileLabel>}
              <StyledDivImgContainer>
                <Image
                  cloudName="ag-millworks"
                  secure="true"
                  publicId={`StilesAndRails/${dsi3}`}
                />
              </StyledDivImgContainer>
            </ColLayout>
          )}
        </RowLayout>
      )}

      {stileCategory === "Pico" && !isSwing && (
        <Video src="https://youtu.be/bEgmGnk3pgU" />
      )}
    </ReusableAccordion>
  );
};

export default memo(StilesRailsAccordian);
